import { memo, useEffect, useMemo, useState } from 'react'
import {
  __,
  concat,
  ifElse,
  map,
  partialRight,
  path,
  pick,
  pipe,
  prop,
  values
} from 'ramda'
import debounce from 'debounce-promise'
import { noop } from 'lodash'
import { createSelector } from 'reselect'

import { authorizeBuildingSearch, searchBuilding } from '@/ports'
import { colors } from '@/styles'
import { headObj, isTruthy, mergeSpec } from '@/utils'
import { Dropdown } from '@/components/form'

import Context from './context'
import Option from './option'
import BuildingDefault from './default-building.png'
import HouseDefault from './default-house.png'

const parseCountryCode = value => {
  switch (value) {
    case 'CAN':
      return 'CA'
    case 'USA':
      return 'US'
    default:
      return value
  }
}

const buildingSelector = createSelector(
  prop('buildings'),
  prop('extra_addresses'),
  path(['files', 'building']),
  (buildings, extras, files) =>
    pipe(
      values,
      concat(__, extras),
      map(
        pipe(
          pick([
            'id',
            'name',
            'street_name',
            'street_number',
            'street_address',
            'city',
            'state',
            'zip',
            'country_code',
            'type',
            'street_name',
            'full_street_name',
            'lat',
            'lon'
          ]),
          mergeSpec({
            name: ifElse(
              pipe(prop('name'), isTruthy),
              prop('name'),
              prop('street_name')
            ),
            image: pipe(prop('id'), partialRight(prop, [files]), headObj),
            value: prop('full_street_name'),
            label: prop('full_street_name'),
            country_code: pipe(prop('country_code'), parseCountryCode)
          })
        )
      )
    )(buildings)
)

const MAX_RESULTS_TO_ALLOW_CREATING = 5

const DropdownIndicator = () => null

const BuildingSearch = ({
  name,
  value,
  onChange,
  placeholder,
  buildingType,
  countryCode,
  hasError,
  onBlur = noop,
  required,
  isLoading,
  setIsLoading,
  ClearButtonComponent,
  clearButton,
  wrapBuildingName,
  newAddressOnClick,
  handleClear,
  showInputText = true
}) => {
  const [text, setText] = useState(value?.full_street_name || '')
  const clear = () => {
    handleClear()
    setText('')
  }
  const handleInputChange = async e => {
    if (e === '') await authorizeBuildingSearch()
    if (text.length === 1 || e !== '') {
      setText(e)
    }
  }
  useEffect(() => {
    authorizeBuildingSearch()
  }, [])
  const getDefaultImage = () =>
    buildingType === 'condo' ? BuildingDefault : HouseDefault
  const onSearch = useMemo(
    () =>
      debounce(async (address, callback) => {
        setIsLoading(true)
        const data = {
          address,
          building_type: buildingType,
          country_code: countryCode
        }
        const response = await searchBuilding({
          body: data
        })
        if (response.response.ok) {
          setIsLoading(false)
          let result = buildingSelector(response.body)
          if (result.length < MAX_RESULTS_TO_ALLOW_CREATING) {
            result.push({
              id: null,
              building_name: '',
              full_street_name: address,
              street_name: address.replace(/^\s*([0-9]+)/, ''),
              street_number: address.match(/^\s*([0-9]+)/)?.[1] || '',
              value: address,
              label: address,
              isNew: true,
              state: ''
            })
          }
          return callback(result)
        }
        setIsLoading(false)
      }, 300),
    [buildingType, countryCode, setIsLoading]
  )

  const hasInputText = showInputText ? !!text : undefined
  const inputValue = showInputText ? text : undefined
  return (
    <Context.Provider
      value={{ searchText: text, defaultImage: getDefaultImage() }}
    >
      <Dropdown
        inputValue={inputValue}
        id="building-search-input"
        data-testid="building-search-input"
        value={value}
        hasInputText={hasInputText}
        loadOptions={onSearch}
        noOptionsMessage={() => null}
        wrapBuildingName={wrapBuildingName}
        newAddressOnClick={value => newAddressOnClick(value)}
        components={{ Option, DropdownIndicator }}
        onChange={onChange}
        onInputChange={handleInputChange}
        cacheOptions
        placeholder={placeholder}
        required={required}
        hasError={hasError}
        onBlur={onBlur}
        isSearchable
        name={name}
        styles={{
          option: (base, state) => ({
            ...base,
            padding: '20px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            borderColor: `${colors.grey}`,
            borderBottom: `1px solid ${colors.grey}`,
            background:
              state.isFocused || state.isSelected
                ? colors.lightGrey
                : 'transparent',
            opacity: state.isFocused || state.isSelected ? 0.5 : 1
          })
        }}
      />
      {!isLoading && text.length > 0 && clearButton && (
        <ClearButtonComponent onClick={clear} />
      )}
    </Context.Provider>
  )
}

export default memo(BuildingSearch)
